import React, { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Plus,
  Edit,
  Trash2,
  X,
  Save,
  Upload,
  Eye,
  EyeOff,
  KeyRound,
  FileText,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { toast } from "react-toastify";
import useSWR from "swr";
import {
  deleteEnvConfig,
  importEnvConfig,
  revealEnvConfig,
  saveEnvConfig,
  type EnvService,
} from "../../api/envConfigApis";
import logger from "../../utils/logger";

interface EnvVariable {
  _id: string;
  key: string;
  value: string | null;
  isSecret: boolean;
  updatedAt: string;
}

interface EnvFormData {
  key: string;
  value: string;
  isSecret: boolean;
}

const SERVICES: { id: EnvService; label: string }[] = [
  { id: "backend", label: "Backend" },
  { id: "video-backend", label: "Video Backend" },
  { id: "stream-backend", label: "Stream Backend" },
];

const EMPTY_FORM: EnvFormData = { key: "", value: "", isSecret: false };

const withoutKey = (map: Record<string, string>, key: string) => {
  const next = { ...map };
  delete next[key];
  return next;
};

const errorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

const EnvSettings: React.FC = () => {
  const [service, setService] = useState<EnvService>("backend");
  const [showForm, setShowForm] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importContent, setImportContent] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const [deleteKey, setDeleteKey] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [revealingKey, setRevealingKey] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, mutate, isLoading } = useSWR<{
    data: EnvVariable[];
    bootstrapKeys: string[];
  }>(`/api/admin/env-config?service=${service}`);

  const variables = data?.data ?? [];
  const bootstrapKeys = data?.bootstrapKeys ?? [];

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<EnvFormData>({ defaultValues: EMPTY_FORM });

  const changeService = (next: EnvService) => {
    setService(next);
    setRevealed({});
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingKey(null);
    reset(EMPTY_FORM);
  };

  const fetchSecret = async (key: string): Promise<string | null> => {
    try {
      const res = await revealEnvConfig({ service, key });
      if (res?.data?.status) return res.data.data.value;
      toast.error(res?.data?.message ?? "Failed to load value");
    } catch (error: unknown) {
      toast.error(errorMessage(error, "Failed to load value"));
    }
    return null;
  };

  const toggleReveal = async (key: string) => {
    if (key in revealed) {
      setRevealed((prev) => withoutKey(prev, key));
      return;
    }
    setRevealingKey(key);
    const value = await fetchSecret(key);
    if (value !== null) setRevealed((prev) => ({ ...prev, [key]: value }));
    setRevealingKey(null);
  };

  const openEdit = async (variable: EnvVariable) => {
    let value = variable.value;
    if (variable.isSecret) {
      value = revealed[variable.key] ?? (await fetchSecret(variable.key));
      if (value === null) return;
    }
    setEditingKey(variable.key);
    reset({
      key: variable.key,
      value: value ?? "",
      isSecret: variable.isSecret,
    });
    setShowForm(true);
  };

  const onSubmit = async (form: EnvFormData) => {
    setIsSubmitting(true);
    try {
      const res = await saveEnvConfig({ service, ...form });
      const resData = res?.data;
      if (resData?.status) {
        toast.success(resData?.message ?? "Variable saved");
        setRevealed((prev) => withoutKey(prev, form.key));
        closeForm();
        mutate();
      } else {
        toast.error(resData?.message ?? "Failed to save variable");
      }
    } catch (error: unknown) {
      toast.error(errorMessage(error, "Failed to save variable"));
      logger.log("Error saving env variable:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      setImportContent(await file.text());
    } catch {
      toast.error("Could not read the file");
    }
  };

  const closeImport = () => {
    setShowImport(false);
    setImportContent("");
  };

  const onImport = async () => {
    setIsImporting(true);
    try {
      const res = await importEnvConfig({ service, content: importContent });
      const resData = res?.data;
      if (resData?.status) {
        const skipped: string[] = resData?.data?.skipped ?? [];
        toast.success(resData?.message ?? "Variables imported");
        if (skipped.length > 0) {
          toast.info(`Kept in .env file only: ${skipped.join(", ")}`);
        }
        setRevealed({});
        closeImport();
        mutate();
      } else {
        toast.error(resData?.message ?? "Failed to import variables");
      }
    } catch (error: unknown) {
      toast.error(errorMessage(error, "Failed to import variables"));
      logger.log("Error importing env variables:", error);
    } finally {
      setIsImporting(false);
    }
  };

  const onDelete = async (key: string) => {
    setIsDeleting(true);
    try {
      const res = await deleteEnvConfig({ service, key });
      const resData = res?.data;
      if (resData?.status) {
        toast.success(resData?.message ?? "Deleted successfully");
        setDeleteKey(null);
        mutate();
      } else {
        toast.error(resData?.message ?? "Failed to delete variable");
      }
    } catch (error: unknown) {
      toast.error(errorMessage(error, "Failed to delete variable"));
    } finally {
      setIsDeleting(false);
    }
  };

  const displayValue = (variable: EnvVariable) => {
    if (!variable.isSecret) return variable.value ?? "";
    return variable.key in revealed ? revealed[variable.key] : "••••••••••••";
  };

  const revealIcon = (key: string) => {
    if (revealingKey === key) {
      return <Loader2 className="h-4 w-4 animate-spin" />;
    }
    if (key in revealed) return <EyeOff className="h-4 w-4" />;
    return <Eye className="h-4 w-4" />;
  };

  return (
    <>
      <div className="min-h-screen max-w-screen w-full ">
        <div className="w-full max-w-full lg:p-4 md:p-4 p-4">
          {/* Header */}
          <div className="mb-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center space-x-3">
                <div className="bg-gray-100 rounded-full p-3">
                  <KeyRound className="h-6 w-6 text-gray-700" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900">
                    Environment Settings
                  </h1>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowImport(true)}
                  className="px-6 py-3 text-gray-700 border border-gray-300 rounded-xl
   hover:bg-gray-100 transition-all font-semibold flex items-center space-x-2"
                >
                  <Upload className="h-5 w-5" />
                  <span>Import .env</span>
                </button>
                <button
                  onClick={() => {
                    setEditingKey(null);
                    reset(EMPTY_FORM);
                    setShowForm(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3
   rounded-xl font-semibold transition-all duration-200
   flex items-center space-x-2 shadow-sm"
                >
                  <Plus className="h-5 w-5" />
                  <span>Add Variable</span>
                </button>
              </div>
            </div>
          </div>

          {/* Restart notice */}
          <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
            <p>
              Changes are read when a service starts. Restart the service after
              saving for new values to take effect.
              {bootstrapKeys.length > 0 && (
                <>
                  {" "}
                  These stay in each service&apos;s .env file and cannot be set
                  here: {bootstrapKeys.join(", ")}.
                </>
              )}
            </p>
          </div>

          {/* Service Tabs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {SERVICES.map((item) => (
              <button
                type="button"
                key={item.id}
                onClick={() => changeService(item.id)}
                className={`bg-white rounded-xl p-4 shadow-sm cursor-pointer transition-all text-left ${
                  service === item.id
                    ? "ring-2 ring-blue-500"
                    : "hover:shadow-md"
                }`}
              >
                <h3 className="text-gray-700 text-sm font-medium">
                  {item.label}
                </h3>
                <p className="text-xs text-gray-400 mt-1">{item.id}/.env</p>
              </button>
            ))}
          </div>

          {/* Variable List */}
          <div
            className="
          bg-white rounded-xl border border-gray-200 shadow-sm
           overflow-hidden"
          >
            <div className="p-4">
              <h2 className="text-xl font-semibold text-gray-800">
                Variables ({variables.length})
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead className="bg-gray-50 border border-gray-100">
                  <tr>
                    <th className="text-left p-2 text-gray-800 font-medium">
                      Sr No
                    </th>
                    <th className="text-left p-2 text-gray-800 font-medium">
                      Key
                    </th>
                    <th className="text-left p-2 text-gray-800 font-medium">
                      Value
                    </th>
                    <th className="text-left p-2 text-gray-800 font-medium">
                      Last Updated
                    </th>
                    <th className="text-left p-2 text-gray-800 font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {variables.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-6 py-16 text-center text-sm text-gray-500"
                      >
                        <div className="flex flex-col items-center justify-center space-y-3">
                          {isLoading ? (
                            <Loader2 className="h-8 w-8 animate-spin text-gray-300" />
                          ) : (
                            <>
                              <FileText className="h-12 w-12 text-gray-300" />
                              <h3 className="text-lg font-medium text-gray-600">
                                No Variables Found
                              </h3>
                              <p className="text-xs text-gray-400">
                                Import a .env file or add variables one by one.
                              </p>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    variables.map((variable, index) => (
                      <tr
                        key={variable._id}
                        className={`border-b border-gray-100 transition-colors ${
                          index % 2 === 0 ? "bg-white" : "bg-blue-50"
                        } hover:bg-gray-50`}
                      >
                        <td className="p-2 text-gray-700 font-normal">
                          {index + 1}
                        </td>

                        <td className="p-2 text-gray-700 font-mono text-sm">
                          {variable.key}
                        </td>

                        <td className="p-2 text-gray-700 font-mono text-sm max-w-md">
                          <div className="flex items-center gap-2">
                            <span className="truncate">
                              {displayValue(variable)}
                            </span>
                            {variable.isSecret && (
                              <button
                                onClick={() => toggleReveal(variable.key)}
                                disabled={revealingKey === variable.key}
                                title={
                                  variable.key in revealed
                                    ? "Hide value"
                                    : "Show value"
                                }
                                className="text-gray-400 hover:text-gray-700 shrink-0"
                              >
                                {revealIcon(variable.key)}
                              </button>
                            )}
                          </div>
                        </td>

                        <td className="p-2 text-gray-700 font-normal whitespace-nowrap">
                          {new Date(variable.updatedAt).toLocaleString()}
                        </td>

                        <td className="p-2">
                          <div className="flex space-x-1">
                            <button
                              title="Edit variable"
                              onClick={() => openEdit(variable)}
                              className="text-blue-500 hover:text-gray-800 p-2 rounded-lg transition-colors"
                            >
                              <Edit className="h-4 w-4" />
                            </button>

                            <button
                              title="Delete variable"
                              onClick={() => setDeleteKey(variable.key)}
                              className="text-red-500 hover:text-red-600 p-2 rounded-lg transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ================================Add/Update Variable Modal========================== */}
          {showForm && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white border border-gray-200 shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b  border-gray-200">
                  <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-semibold text-gray-700">
                      {editingKey ? "Update Variable" : "Add New Variable"}
                    </h2>
                    <button
                      onClick={closeForm}
                      className="text-gray-700/70 hover:text-gray-700 p-2 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  <div>
                    <label
                      htmlFor="env-key"
                      className="block text-gray-700 text-sm font-medium mb-2"
                    >
                      Key *
                    </label>
                    <input
                      id="env-key"
                      {...register("key", {
                        required: "Key is required",
                        pattern: {
                          value: /^[A-Za-z_]\w*$/,
                          message:
                            "Only letters, numbers and underscores; cannot start with a number",
                        },
                        validate: (value) =>
                          !bootstrapKeys.includes(value) ||
                          `${value} must stay in the service's .env file`,
                      })}
                      readOnly={!!editingKey}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg font-mono
text-gray-800 focus:outline-none focus:ring-2 placeholder-gray-400 focus:ring-blue-400 focus:border-transparent transition-all read-only:bg-gray-100"
                      placeholder="e.g. SMTP_HOST"
                    />
                    {errors.key && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.key.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="env-value"
                      className="block text-gray-700 text-sm font-medium mb-2"
                    >
                      Value
                    </label>
                    <textarea
                      id="env-value"
                      {...register("value")}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg font-mono
text-gray-800 focus:outline-none focus:ring-2 placeholder-gray-400 focus:ring-blue-400 focus:border-transparent transition-all"
                      placeholder="Enter value"
                      rows={3}
                      spellCheck={false}
                    />
                  </div>

                  <label className="flex items-center gap-3 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      {...register("isSecret")}
                      className="h-4 w-4"
                    />
                    <span>Secret — hide the value in the list</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-4 p-6">
                  <button
                    type="button"
                    onClick={closeForm}
                    className="px-6 py-3 text-gray-700/70 hover:text-gray-700 border  border-gray-300 rounded-xl hover:bg-gray-100 transition-all font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    disabled={isSubmitting}
                    onClick={handleSubmit(onSubmit)}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white
    rounded-xl transition-all font-medium flex items-center space-x-2
    disabled:opacity-50 disabled:cursor-not-allowed min-w-[180px] justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="h-5 w-5" />
                        <span>
                          {editingKey ? "Update Variable" : "Add Variable"}
                        </span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ================================Import .env Modal========================== */}
          {showImport && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white border border-gray-200 shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b  border-gray-200">
                  <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-semibold text-gray-700">
                      Import .env —{" "}
                      {SERVICES.find((item) => item.id === service)?.label}
                    </h2>
                    <button
                      onClick={closeImport}
                      className="text-gray-700/70 hover:text-gray-700 p-2 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  <p className="text-sm text-gray-600">
                    Choose the service&apos;s .env file or paste its contents.
                    Existing keys are overwritten; keys not in the file are
                    left as they are.
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={onFileSelected}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-all font-medium flex items-center space-x-2"
                  >
                    <Upload className="h-4 w-4" />
                    <span>Choose File</span>
                  </button>
                  <textarea
                    value={importContent}
                    onChange={(e) => setImportContent(e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg font-mono text-sm
text-gray-800 focus:outline-none focus:ring-2 placeholder-gray-400 focus:ring-blue-400 focus:border-transparent transition-all"
                    placeholder={"KEY=value\nANOTHER_KEY=value"}
                    rows={12}
                    spellCheck={false}
                  />
                </div>

                <div className="flex justify-end space-x-4 p-6">
                  <button
                    type="button"
                    onClick={closeImport}
                    className="px-6 py-3 text-gray-700/70 hover:text-gray-700 border  border-gray-300 rounded-xl hover:bg-gray-100 transition-all font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    disabled={isImporting || !importContent.trim()}
                    onClick={onImport}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white
    rounded-xl transition-all font-medium flex items-center space-x-2
    disabled:opacity-50 disabled:cursor-not-allowed min-w-[180px] justify-center"
                  >
                    {isImporting ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>Importing...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="h-5 w-5" />
                        <span>Import</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {deleteKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-xs">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">
              Delete Variable?
            </h3>
            <p className="text-gray-700 mb-6 break-all">
              Are you sure you want to delete {deleteKey}?
            </p>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
                onClick={() => setDeleteKey(null)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-lg border border-white/20 bg-red-500
    text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed
    min-w-[100px] flex items-center justify-center gap-2"
                disabled={isDeleting}
                onClick={() => onDelete(deleteKey)}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EnvSettings;
